import * as React from 'react';
import ReactInputMask from 'react-input-mask';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { Paperclip } from 'lucide-react';
import { Button } from './button';

const inputVariants = cva(
  'flex h-10 w-full rounded-md border border-primary-lightest bg-background px-3 py-2 text-neutral ring-offset-background placeholder:text-neutral-light text-base md:text-sm ' +
    'hover:text-neutral-dark hover:border-neutral-light hover:bg-bright ' +
    'focus-visible:outline-none focus-visible:text-neutral-dark focus-visible:border-neutral-dark ' +
    'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground ' +
    'disabled:cursor-not-allowed disabled:bg-bright disabled:text-neutral-light disabled:hover:bg-bright disabled:hover:border-primary-lightest ' +
    'transition-colors duration-150 ' +
    '[&[aria-invalid=true]]:border-danger',
  {
    variants: {
      endIcon: {
        true: 'pe-9'
      },
      startIcon: {
        true: 'ps-9'
      }
    },
    defaultVariants: {
      endIcon: false,
      startIcon: false
    }
  }
);

const Input = React.forwardRef(({ className, type, startIcon, endIcon, ...props }, ref) => {
  return (
    <input
      type={type}
      className={cn('c-input', inputVariants({ startIcon, endIcon }), className)}
      ref={ref}
      {...props}
    />
  );
});
Input.displayName = 'Input';

const InputMask = React.forwardRef(({ className, disabled, ...props }, ref) => {
  return (
    <ReactInputMask
      disabled={disabled}
      {...props}
    >
      {inputProps => (
        <Input
          ref={ref}
          disabled={disabled || inputProps.disabled}
          className={cn(className)}
          {...inputProps}
        />
      )}
    </ReactInputMask>
  );
});
InputMask.displayName = 'InputMask';

const InputPassword = React.forwardRef(({ className, ...props }, ref) => {
  const [showPassword, setShowPassword] = React.useState(false);

  return (
    <div className="relative">
      <Input
        ref={ref}
        type={showPassword ? 'text' : 'password'}
        className={cn('peer', className)}
        endIcon
        {...props}
      />
      <button
        type="button"
        aria-label="Alterar visibilidade da senha"
        title="Alterar visibilidade da senha"
        className="absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-primary-dark peer-disabled:opacity-50 [&_svg]:size-4"
        onClick={() => setShowPassword(!showPassword)}
      >
        {showPassword ? <FiEye aria-hidden="true" /> : <FiEyeOff aria-hidden="true" />}
      </button>
    </div>
  );
});
InputPassword.displayName = 'InputPassword';

const InputFile = React.forwardRef(({ className, buttonClassName, file, variant = 'input', buttonVariant = 'default', buttonLabel = 'Enviar', label = 'Clique para inserir um arquivo...', ...props }, ref) => {
  const isInput = variant === 'input';

  return (
    <label
      ref={ref}
      className="relative block cursor-pointer"
    >
      <input
        className="hidden"
        type="file"
        {...props}
      />
      {isInput ? (
        <>
          <span
            className={cn(
              'flex h-10 w-full rounded-md border border-solid border-primary-lightest bg-background px-3 py-2 text-base font-light text-neutral-light ring-offset-background md:text-sm',
              'hover:border-neutral-light hover:bg-bright hover:text-neutral-dark',
              'focus-visible:border-neutral-dark focus-visible:text-neutral-dark focus-visible:outline-none',
              'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'transition-colors duration-150',
              '[&[aria-invalid=true]]:border-danger',
              'pe-9',
              className
            )}
          >
            {file ? file?.name : label}
          </span>
          <span className="absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-primary-dark peer-disabled:opacity-50 [&_svg]:size-4">
            <Paperclip aria-hidden="true" />
          </span>
        </>
      ) : (
        <Button
          variant={buttonVariant}
          className={buttonClassName}
          asChild
        >
          <span>{buttonLabel}</span>
        </Button>
      )}
    </label>
  );
});
InputFile.displayName = 'InputFile';

export { Input, InputMask, InputPassword, InputFile, inputVariants };
